import { useContext, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useLocalStorage } from '~/utils/useLocalStorage';
import { useToastError } from '~/utils/useToastError';
import { Button, InputBar, Textarea, IconButton } from '~/ui/index';
import styles from './DishForm.module.scss';
import { useTranslation } from 'react-i18next';
import { AuthenticationContext } from '../Authentication/AuthenticationProvider';
import {
  checkIfUserAssetAdmin,
  checkIfUserSuperAdmin,
} from '~/utils/getUserRole';
import CustomSelect from '../Shared/CustomSelect/CustomSelect';
import { Modal } from '../Shared/Modal/Modal';
import PhotoPickerWithUpload, {
  IPhoto,
} from '../Shared/PhotoPickerWithUpload/PhotoPickerWithUpload';
import PhotoIcon from '~/assets/svg/newSvg/picture.svg?react';
import CloseIcon from '~/assets/svg/newSvg/close-cross.svg?react';

export interface DishItem {
  id: number;
  wares_name: string;
  ingredients: string;
  price_list: { price: string };
  weight: string;
  photos: { id: number; full_photo_url: string }[];
  measure: string;
  asset_id?: number;
}

export interface DishFormValues {
  wares_name: string;
  ingredients: string;
  price: string;
  weight: string;
  measure: string | { id: number; name: string };
  photos: IPhoto[];
}

interface DishFormProps {
  onSubmit: (values: DishFormValues) => void;
  dishItem?: DishItem;
  foodMenuId?: number;
  storageKey?: string;
  assetId?: number | null;
}

export const DishForm = ({
  onSubmit,
  dishItem,
  foodMenuId,
  storageKey,
  assetId,
}: DishFormProps): JSX.Element => {
  const toastError = useToastError();
  const { t } = useTranslation();
  const { user } = useContext(AuthenticationContext);
  const isEnoughAccess =
    checkIfUserSuperAdmin(user?.data.role_name) ||
    checkIfUserAssetAdmin(user?.data.role_name);

  const isSuperAdmin = checkIfUserSuperAdmin(user?.data.role_name);
  const isAssetAdmin = checkIfUserAssetAdmin(user?.data.role_name);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { initialValues, storedInitialValues, clear, create } =
    useLocalStorage<DishFormValues>({
      initialValues: {
        wares_name: '',
        ingredients: '',
        price: '',
        weight: '',
        measure: { id: 1, name: t('g') },
        photos: [],
      },
      key: storageKey && `${storageKey}-v1`,
      exclude: ['photos'],
    });

  const formik = useFormik<DishFormValues>({
    initialValues: storedInitialValues,

    onSubmit: async (values: DishFormValues) => {
      const correctValues = {
        ...values,
        measure:
          typeof values.measure === 'object' && values.measure !== null
            ? values.measure.name
            : values.measure,
      };
      try {
        await onSubmit(correctValues);
        clear();
        formik.resetForm({ values: initialValues });
      } catch (error) {
        toastError(error);
      }
    },
  });

  create(formik.values);

  const setValues = formik.setValues;
  const setFieldValue = formik.setFieldValue;

  useEffect(() => {
    if (!dishItem) return;
    setFieldValue('food_menus_id', foodMenuId);

    setValues({
      wares_name: dishItem.wares_name,
      ingredients: dishItem.ingredients,
      measure:
        dishItem.measure.toLowerCase() === 'г'
          ? { id: 1, name: t('g') }
          : dishItem.measure.toLowerCase() === 'мл'
            ? { id: 2, name: t('ml') }
            : { id: 3, name: t('pcs') },
      price: dishItem?.price_list?.price || '',
      weight: dishItem.weight || '',
      photos: (dishItem.photos || []).map(p => ({
        id: p.id,
        full_photo_url: p.full_photo_url,
      })),
    });
  }, [foodMenuId, setValues, dishItem, setFieldValue]);

  const onImagesChange = (image: IPhoto) => {
    const photoExists = formik.values.photos?.find(
      photo => photo.id === image.id
    );

    if (photoExists) {
      formik.setFieldValue(
        'photos',
        formik.values.photos.filter(photo => photo.id !== image.id)
      );
    } else {
      formik.setFieldValue('photos', [...formik.values.photos, image]);
    }
  };

  const handleRemoveImage = (id: number) => {
    formik.setFieldValue(
      'photos',
      formik.values.photos.filter(photo => photo?.id !== id)
    );
  };

  const handleResetImages = () => {
    formik.setFieldValue('photos', []);
  };

  return (
    <div className={styles.formWrapper}>
      <form
        className={styles['form']}
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <div className={styles.formItems}>
          <InputBar
            name="wares_name"
            value={formik.values.wares_name}
            label={t('name')}
            star={true}
            placeholder={t('dish_name')}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={!isEnoughAccess}
          />
          <Textarea
            rows={4}
            label={t('composition')}
            placeholder={t('dish_description')}
            maxLength={600}
            value={formik.values.ingredients}
            onChange={formik.handleChange}
            name="ingredients"
            star={false}
            disabled={!isEnoughAccess}
          />
          <div className={styles.priceWrapper}>
            <InputBar
              name="price"
              value={formik.values.price}
              label={`${t('price')} ₽`}
              star={true}
              placeholder="XXX"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
            />
            <InputBar
              type="numeric"
              name="weight"
              value={formik.values.weight}
              label={`${t('quantity_full')}`}
              star={false}
              placeholder="XXX"
              onChange={event => {
                const weight = event.target.value;
                formik.setFieldValue('weight', weight);
              }}
              onBlur={formik.handleBlur}
            />
            <CustomSelect
              className={styles.measure}
              label={t('measure')}
              value={formik.values.measure}
              options={[
                { id: 1, name: t('g') },
                { id: 2, name: t('ml') },
                { id: 3, name: t('pcs') },
              ]}
              disabled={!isSuperAdmin && !isAssetAdmin}
              placeholder={`${t('g')}, ${t('ml')}, ${t('pcs')}`}
              onChange={value => formik.setFieldValue('measure', value)}
            />
          </div>

          <div className={styles.imagesContainer}>
            <ul className={styles.imagesList}>
              {formik?.values?.photos?.map(image => (
                <div key={image.id} className={styles.imageWrapper}>
                  <img
                    src={image.full_photo_url}
                    alt=""
                    className={styles.image}
                  />
                  {isEnoughAccess && (
                    <button
                      type="button"
                      className={styles.deleteImageButton}
                      onClick={() => handleRemoveImage(image.id)}
                    >
                      <CloseIcon className={styles.closeIcon} />
                    </button>
                  )}
                </div>
              ))}
            </ul>
            {isEnoughAccess && (
              <IconButton
                onClick={() => setIsModalOpen(true)}
                className={styles.pickPhotoButton}
                isDisabled={!assetId}
              >
                <PhotoIcon />
                <span className={styles.pickPhotoText}>
                  {t('choose_photo')}
                </span>
              </IconButton>
            )}
          </div>
        </div>

        <Button
          type="submit"
          text={t('save_n_exit')}
          className={styles.formBtn}
          disabled={!((formik.isValid && formik.dirty) || formik.isSubmitting)}
        />

        <Modal
          modalStyles={styles.photosModal}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        >
          <PhotoPickerWithUpload
            module="ifcm_wares"
            assetId={assetId || null}
            initialPhotos={
              dishItem
                ? (dishItem.photos || []).map(p => ({
                    id: p.id,
                    full_photo_url: p.full_photo_url,
                  }))
                : []
            }
            selectedImages={formik.values.photos || []}
            onImageChange={onImagesChange}
            onCloseClick={() => setIsModalOpen(false)}
            onResetClick={handleResetImages}
          />
        </Modal>
      </form>
    </div>
  );
};
